import { useState } from 'react'
import { useParams } from 'react-router'

import type { Permisos } from '../api/capacidades'
import { Aviso, Boton, Tarjeta } from '../componentes/ui'
import { Editor } from '../motor/Editor'
import { campoPorNombre, useEsquema } from '../motor/esquema'
import { Listado } from '../motor/Listado'
import { AvisosDeCobro } from './Cobro'
import { DevolverDinero, type Devuelto } from './DevolverDinero'
import { cuentasDelPago, dinero, type DevolucionDelPedido, nombreDePasarela, preguntaAlCambiarDeEstado, quienDevolvio } from './tiendaEnLlano'

/**
 * Los pedidos (CLAUDE.md §23): el listado, con el total en su moneda y la búsqueda por
 * número, nombre o correo del comprador; y la ficha: lo que compró, cuánto, quién y cómo
 * se pagó, y lo que el cliente puede hacer —cambiar el estado, anotar y **reembolsar** si
 * se cobró por pasarela, todo o una parte—. Los importes vienen en céntimos y se enseñan en
 * la unidad, con las mismas cuentas que la tienda (`@mc/comercio`: un peso chileno no tiene
 * céntimos).
 */
type Linea = { nombre?: string; variante?: string; sku?: string; cantidad?: number; precio?: number; importe?: number }
type Pago = {
  estado?: null | string
  pasarela?: null | string
  importe?: null | number
  moneda?: null | string
  pagadoEn?: null | string
  iniciadoEn?: null | string
  ultimoError?: null | string
  importeRecibido?: null | number
  monedaRecibida?: null | string
  devuelto?: null | number
  reembolsos?: null | DevolucionDelPedido[]
}

export const ListaDePedidos = ({ espacio, sitio, base, descripcion, vacio }: { base: string; descripcion?: string; espacio: number; sitio: number; vacio?: string }) => (
  <Listado
    aviso={<AvisosDeCobro espacio={espacio} sitio={sitio} />}
    base={base}
    coleccion="pedidos"
    columnasPropias={{ total: { clave: 'total', etiqueta: 'Total', celda: (doc) => <span className="tabular-nums">{dinero(doc.total, doc.moneda)}</span> } }}
    descripcion={descripcion}
    espacio={espacio}
    puedeCrear={false}
    sitio={sitio}
    vacio={vacio}
  />
)

const CajaDePago = ({ valores }: { valores: Record<string, unknown> }) => {
  const { data: esquema } = useEsquema('pedidos')
  const pago = (valores.pago ?? {}) as Pago
  const estado = pago.estado ?? 'sin-pasarela'
  // La etiqueta del estado, del esquema del núcleo: aquí no hay copia de la lista.
  const etiquetas = esquema ? (campoPorNombre(esquema.campos, 'pago')?.campos ?? []).find((c) => c.nombre === 'estado')?.opciones : undefined
  const etiqueta = etiquetas?.find((o) => o.valor === estado)?.etiqueta ?? estado
  const pasarela = nombreDePasarela(pago.pasarela)
  // La forma de pago que eligió el comprador, con su nombre del esquema (Yape, Plin…). Vacía en los pedidos de antes.
  const forma = valores.formaDePago && valores.formaDePago !== 'pasarela' ? (campoPorNombre(esquema?.campos ?? [], 'formaDePago')?.opciones?.find((o) => o.valor === valores.formaDePago)?.etiqueta ?? null) : null
  const importeDistinto = typeof pago.importeRecibido === 'number'
  return (
    <Tarjeta>
      <p className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">Pago</p>
      <p className="text-sm">
        <span className="font-medium">{etiqueta}</span>
        {pasarela && estado !== 'sin-pasarela' ? <span className="text-tenue"> · con {pasarela}</span> : null}
      </p>
      {typeof pago.importe === 'number' && <p className="text-xs text-tenue">Se pidió cobrar {dinero(pago.importe, pago.moneda ?? valores.moneda)}</p>}
      {typeof pago.pagadoEn === 'string' && <p className="text-xs text-tenue">Pagado el {new Date(pago.pagadoEn).toLocaleString('es')}</p>}
      {estado === 'sin-pasarela' && (
        <p className="mt-2 text-xs text-tinta-2">
          {forma
            ? `Eligió pagar con ${forma}. Nadie nos avisa de ese pago: cuando lo veas${forma === 'Yape' || forma === 'Plin' ? ` en tu app de ${forma}` : ''}, cambia el estado a «Pagado».`
            : 'Se paga aparte (transferencia, contra entrega…). Cuando te pague, cambia el estado a «Pagado».'}
        </p>
      )}
      {estado === 'pendiente' && typeof pago.iniciadoEn === 'string' && Date.now() - new Date(pago.iniciadoEn).getTime() > 60 * 60_000 && (
        <p className="mt-2 text-xs text-tinta-2">Lleva más de una hora sin confirmarse. Si el comprador dice que pagó, míralo en {pasarela || 'la pasarela'}: puede que no nos estén llegando sus avisos.</p>
      )}
      {typeof pago.ultimoError === 'string' && pago.ultimoError && <p className="mt-1 text-xs text-error">{pago.ultimoError}</p>}
      {importeDistinto && (
        <div className="mt-3">
          <Aviso tono="error">
            <strong>El importe no cuadra.</strong> {pasarela || 'La pasarela'} confirmó un cobro de {dinero(pago.importeRecibido, pago.monedaRecibida ?? pago.moneda)}, y el pedido es de {dinero(pago.importe ?? valores.total, pago.moneda ?? valores.moneda)}. No se ha marcado como pagado: compruébalo en {pasarela || 'la pasarela'} antes de enviar nada.
          </Aviso>
        </div>
      )}
      <Devoluciones moneda={pago.moneda ?? valores.moneda} pago={pago} total={valores.total} />
    </Tarjeta>
  )
}

/** Lo devuelto de un pedido: cuánto de cuánto, y cada devolución con su fecha, quién y por qué. */
const Devoluciones = ({ pago, total, moneda }: { moneda: unknown; pago: Pago; total: unknown }) => {
  const lista = (pago.reembolsos ?? []).filter((d) => typeof d.importe === 'number')
  if (lista.length === 0) return null
  const { cobrado, devuelto, queda } = cuentasDelPago(pago, total)
  return (
    <div className="mt-3 border-t border-linea pt-3">
      <p className="text-sm">
        <span className="font-medium">Devuelto {dinero(devuelto, moneda)}</span>
        <span className="text-tenue"> de {dinero(cobrado, moneda)}</span>
        {queda > 0 && <span className="text-tenue"> · quedan {dinero(queda, moneda)}</span>}
      </p>
      <ul className="mt-2 flex flex-col gap-2">
        {lista.map((d, i) => (
          <li className="text-xs" key={d.idExterno ?? i}>
            <span className="font-medium tabular-nums">{dinero(d.importe, moneda)}</span>
            {typeof d.fecha === 'string' && <span className="text-tenue"> · {new Date(d.fecha).toLocaleString('es')}</span>}
            <span className="text-tenue"> · {quienDevolvio(d, pago.pasarela)}</span>
            {d.motivo && <span className="block text-tinta-2 italic">«{d.motivo}»</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

type Entrega = {
  tipo?: null | string
  zona?: null | string
  plazo?: null | string
  tarifa?: null | number
  gratis?: null | boolean
  paisNombre?: null | string
  lugares?: null | Array<{ nivel?: string; nombre?: string }>
  direccion?: null | string
  referencia?: null | string
  horario?: null | string
  distanciaKm?: null | number
  latitud?: null | number
  longitud?: null | number
}

/**
 * Cómo llega el pedido (CLAUDE.md §23): el destino que se **copió** al pedir —país, cada
 * nivel con su nombre, la dirección, la referencia, la zona, el plazo y la distancia—, o el
 * punto de recojo. Los pedidos de antes, sin destino copiado, enseñan la zona y la dirección
 * que se escribieron.
 */
const CajaDeEntrega = ({ valores }: { valores: Record<string, unknown> }) => {
  const e = (valores.entrega ?? {}) as Entrega
  const zonaVieja = typeof valores.envioZona === 'string' ? valores.envioZona : null
  const direccionVieja = typeof valores.envioDireccion === 'string' ? valores.envioDireccion : null
  if (!e.tipo && !zonaVieja && !direccionVieja) return null
  const recojo = e.tipo === 'recojo'
  const lugares = (e.lugares ?? []).filter((l) => l.nombre)
  return (
    <Tarjeta>
      <p className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">{recojo ? 'Recojo en tienda' : 'Envío'}</p>
      {e.tipo ? (
        <>
          <p className="font-medium">{e.zona}</p>
          {e.plazo && <p className="text-xs text-tenue">{recojo ? `Listo: ${e.plazo}` : `Plazo: ${e.plazo}`}</p>}
          {e.direccion && <p className="mt-2 text-sm whitespace-pre-wrap">{e.direccion}</p>}
          {e.referencia && <p className="text-sm text-tinta-2">Referencia: {e.referencia}</p>}
          {recojo && e.horario && <p className="text-sm text-tinta-2">Horario: {e.horario}</p>}
          {lugares.length > 0 && (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 text-xs">
              {e.paisNombre && (
                <>
                  <dt className="text-tenue">País</dt>
                  <dd>{e.paisNombre}</dd>
                </>
              )}
              {lugares.map((l, i) => (
                <div className="contents" key={i}>
                  <dt className="text-tenue">{l.nivel || 'Lugar'}</dt>
                  <dd>{l.nombre}</dd>
                </div>
              ))}
            </dl>
          )}
          {typeof e.distanciaKm === 'number' && <p className="mt-2 text-xs text-tenue">A {String(e.distanciaKm).replace('.', ',')} km por carretera de tu local</p>}
          {typeof e.latitud === 'number' && typeof e.longitud === 'number' && (
            <a className="mt-1 inline-block text-xs text-violeta underline" href={`https://www.google.com/maps/search/?api=1&query=${e.latitud},${e.longitud}`} rel="noreferrer" target="_blank">
              Ver en el mapa
            </a>
          )}
          {e.gratis && <p className="mt-2 text-xs text-ok">Envío gratis por el importe del pedido (la zona cobra {dinero(e.tarifa, valores.moneda)}).</p>}
        </>
      ) : (
        <>
          {zonaVieja && <p className="font-medium">{zonaVieja}</p>}
          {direccionVieja && <p className="mt-1 text-sm whitespace-pre-wrap text-tinta-2">{direccionVieja}</p>}
        </>
      )}
    </Tarjeta>
  )
}

/** Lo que se le dice a quien acaba de devolver dinero: cuánto, y qué ha pasado con el pedido y sus existencias. */
const textoDeDevuelto = (r: Devuelto, pasarela: unknown): string =>
  r.completo
    ? `${nombreDePasarela(pasarela) || 'La pasarela'} ha devuelto ${dinero(r.importe, r.moneda)}. Ya está devuelto todo lo cobrado: el pedido queda como «Reembolsado» y sus unidades vuelven a las existencias.`
    : `${nombreDePasarela(pasarela) || 'La pasarela'} ha devuelto ${dinero(r.importe, r.moneda)}. El pedido sigue como estaba y sus existencias no cambian; quedan ${dinero(r.queda, r.moneda)} por devolver.`

export const FichaDePedido = ({ espacio, sitio, base, permisos }: { base: string; espacio: number; permisos: Permisos; sitio: number }) => {
  const { id } = useParams()
  const pedido = Number(id)
  const [mensaje, setMensaje] = useState<null | { texto: string; tono: 'error' | 'ok' }>(null)
  const [devolviendo, setDevolviendo] = useState(false)

  return (
    <Editor
      acciones={({ valores, recargar }) => {
        const pago = (valores.pago ?? {}) as Pago
        const cuentas = cuentasDelPago(pago, valores.total)
        // Reembolsar es de quien gestiona los pedidos (el núcleo pide `tienda.pedidos`), y solo de lo cobrado por pasarela que queda por devolver.
        if (pago.estado !== 'pagado' || !pago.pasarela || !permisos.tiene('tienda.pedidos') || cuentas.queda <= 0) return null
        const moneda = String(pago.moneda ?? valores.moneda ?? 'PEN')
        return (
          <>
            <Boton onClick={() => setDevolviendo(true)} pequeno>
              {cuentas.devuelto > 0 ? 'Devolver más' : 'Reembolsar'}
            </Boton>
            {devolviendo && (
              <DevolverDinero
                cerrar={() => setDevolviendo(false)}
                cobrado={cuentas.cobrado}
                comprador={String(valores.compradorEmail ?? '')}
                devuelto={cuentas.devuelto}
                espacio={espacio}
                estado={valores.estado}
                existenciasDescontadas={valores.existenciasDescontadas}
                hecho={(r: Devuelto) => {
                  setDevolviendo(false)
                  setMensaje({ texto: textoDeDevuelto(r, pago.pasarela), tono: 'ok' })
                  recargar()
                }}
                moneda={moneda}
                pasarela={pago.pasarela}
                pedido={pedido}
                queda={cuentas.queda}
              />
            )}
          </>
        )
      }}
      antesDeGuardar={({ antes, despues }) => preguntaAlCambiarDeEstado(antes, despues)}
      base={base}
      cabecera={({ valores }) => {
        const lineas = (Array.isArray(valores.lineas) ? valores.lineas : []) as Linea[]
        return (
          <div className="flex flex-col gap-4">
            {mensaje && <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso>}
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
              <Tarjeta className="!p-0">
                <table className="w-full text-sm">
                  <thead className="bg-papel-2 text-left">
                    <tr>
                      <th className="p-3 font-medium">Producto</th>
                      <th className="p-3 text-right font-medium">Cantidad</th>
                      <th className="p-3 text-right font-medium">Precio</th>
                      <th className="p-3 text-right font-medium">Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineas.map((l, i) => (
                      <tr className="border-t border-linea" key={i}>
                        <td className="p-3">
                          {l.nombre}
                          {l.variante && <span className="text-tenue"> · {l.variante}</span>}
                          {l.sku && <span className="block text-xs text-tenue">{l.sku}</span>}
                        </td>
                        <td className="p-3 text-right tabular-nums">{l.cantidad}</td>
                        <td className="p-3 text-right tabular-nums">{dinero(l.precio, valores.moneda)}</td>
                        <td className="p-3 text-right tabular-nums">{dinero(l.importe, valores.moneda)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t border-linea text-right">
                    <tr>
                      <td className="px-3 pt-3 text-tenue" colSpan={3}>
                        Subtotal
                      </td>
                      <td className="px-3 pt-3 tabular-nums">{dinero(valores.subtotal, valores.moneda)}</td>
                    </tr>
                    {typeof valores.envio === 'number' && (
                      <tr>
                        <td className="px-3 text-tenue" colSpan={3}>
                          Envío{typeof valores.envioEtiqueta === 'string' && valores.envioEtiqueta ? ` · ${valores.envioEtiqueta}` : ''}
                        </td>
                        <td className="px-3 tabular-nums">{dinero(valores.envio, valores.moneda)}</td>
                      </tr>
                    )}
                    {typeof valores.impuestos === 'number' && valores.impuestos > 0 && (
                      <tr>
                        <td className="px-3 text-tenue" colSpan={3}>
                          Impuestos
                        </td>
                        <td className="px-3 tabular-nums">{dinero(valores.impuestos, valores.moneda)}</td>
                      </tr>
                    )}
                    <tr className="font-semibold">
                      <td className="p-3" colSpan={3}>
                        Total
                      </td>
                      <td className="p-3 tabular-nums">{dinero(valores.total, valores.moneda)}</td>
                    </tr>
                  </tfoot>
                </table>
              </Tarjeta>
              <div className="flex flex-col gap-4">
                <Tarjeta>
                  <p className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">Comprador</p>
                  <p className="font-medium">{String(valores.compradorNombre ?? '')}</p>
                  <p className="text-sm">
                    <a className="text-violeta underline" href={`mailto:${String(valores.compradorEmail ?? '')}`}>
                      {String(valores.compradorEmail ?? '')}
                    </a>
                  </p>
                  {typeof valores.compradorTelefono === 'string' && valores.compradorTelefono && <p className="text-sm">{valores.compradorTelefono}</p>}
                  {typeof valores.compradorNota === 'string' && valores.compradorNota && <p className="mt-2 text-sm text-tinta-2 italic">«{valores.compradorNota}»</p>}
                </Tarjeta>
                <CajaDeEntrega valores={valores} />
                <CajaDePago valores={valores} />
              </div>
            </div>
          </div>
        )
      }}
      coleccion="pedidos"
      espacio={espacio}
      id={pedido}
      // Lo que ya enseñan las tarjetas de arriba, en su moneda: no se repite abajo en céntimos.
      ocultarCampos={[
        'lineas',
        'pago',
        'existenciasDescontadas',
        'numero',
        'fecha',
        'compradorNombre',
        'compradorEmail',
        'compradorTelefono',
        'compradorNota',
        'moneda',
        'subtotal',
        'envio',
        'envioEtiqueta',
        'impuestos',
        'total',
        'envioZona',
        'envioDireccion',
        'entrega',
      ]}
      puedeBorrar={false}
      sitio={sitio}
    />
  )
}
